import React, { useState, useEffect, useCallback, useRef } from "react";
import { TextField, Button, MenuItem, Grid, Typography, Box, IconButton, CircularProgress, Snackbar, Alert, Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import Notes from "./header";
import { Link } from "react-router-dom";
import DeleteIcon from '@mui/icons-material/Delete';
import FooterManual from "./footerManual";
import Avatar from '@mui/material/Avatar';
import { useNavigate } from "react-router-dom";

// ==================== COUNTRY CODES DATA ====================
let COUNTRY_CODES = [];

// ==================== SEARCHABLE COUNTRY CODE SELECT ====================
const SearchableCountryCodeSelect = ({ value, onChange, label, options = COUNTRY_CODES }) => {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredOptions, setFilteredOptions] = useState(options);
  const inputRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    if (searchTerm) {
      const filtered = options.filter(item =>
        item.country.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.code.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredOptions(filtered);
    } else {
      setFilteredOptions(options);
    }
  }, [searchTerm, options]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        handleClose();
      }
    };

    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open]);

  const handleOpen = () => {
    setOpen(true);
    setSearchTerm('');
    setTimeout(() => {
      inputRef.current?.focus();
    }, 100);
  };

  const handleClose = () => {
    setOpen(false);
    setSearchTerm('');
  };

  const handleSelect = (code) => {
    onChange({ target: { name: 'countryCode', value: code } });
    handleClose();
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange({ target: { name: 'countryCode', value: '' } });
    setSearchTerm('');
  };

  const selectedItem = options.find(item => item.code === value);

  return (
    <div ref={containerRef} style={{ position: 'relative', width: '100%' }}>
      <div style={{ position: 'relative' }}>
        <input
          ref={inputRef}
          type="text"
          value={open ? searchTerm : (selectedItem ? `${selectedItem.flag} ${selectedItem.code}` : '')}
          onChange={(e) => {
            if (open) {
              setSearchTerm(e.target.value);
            }
          }}
          onClick={handleOpen}
          placeholder={!selectedItem ? "Select country code" : ""}
          readOnly={!open}
          style={{
            width: '100%',
            padding: '16.5px 40px 16.5px 14px',
            border: '1px solid #d32f2f',
            borderRadius: '4px',
            fontSize: '16px',
            outline: 'none',
            cursor: open ? 'text' : 'pointer',
            backgroundColor: 'white',
            transition: 'all 0.2s',
            height: '56px',
            ...(open && {
              borderColor: '#d32f2f',
              borderWidth: '2px',
            })
          }}
        />

        {value && !open && (
          <button
            onClick={handleClear}
            style={{
              position: 'absolute',
              right: '32px',
              top: '50%',
              transform: 'translateY(-50%)',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#9ca3af',
              transition: 'color 0.2s',
              zIndex: 2
            }}
            onMouseEnter={(e) => { e.currentTarget.style.color = '#d32f2f'; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = '#9ca3af'; }}
            type="button"
          >
            <svg style={{ width: '16px', height: '16px', pointerEvents: 'none' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}

        <svg
          style={{
            position: 'absolute',
            right: '12px',
            top: '50%',
            transform: 'translateY(-50%)',
            width: '20px',
            height: '20px',
            color: open ? '#d32f2f' : '#9ca3af',
            pointerEvents: 'none',
            zIndex: 2
          }}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          {open ? (
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          ) : (
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          )}
        </svg>
      </div>

      {open && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          right: 0,
          maxHeight: '400px',
          overflow: 'auto',
          zIndex: 1300,
          marginTop: '4px',
          backgroundColor: 'white',
          border: '1px solid #e5e7eb',
          borderRadius: '6px',
          boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)'
        }}>
          {filteredOptions.length === 0 ? (
            <div style={{ padding: '12px 16px', color: '#6b7280', fontSize: '14px' }}>
              No results found
            </div>
          ) : (
            filteredOptions.map((item, index) => {
              const isSelected = value === item.code;
              return (
                <div
                  key={`${item.code}-${index}`}
                  onClick={() => handleSelect(item.code)}
                  style={{
                    padding: '12px 16px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: 500,
                    transition: 'all 0.2s',
                    backgroundColor: isSelected ? 'rgba(211, 47, 47, 0.12)' : 'white',
                    color: isSelected ? '#d32f2f' : '#374151',
                    borderBottom: index < filteredOptions.length - 1 ? '1px solid #f3f4f6' : 'none',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = isSelected ? 'rgba(211, 47, 47, 0.2)' : 'rgba(211, 47, 47, 0.08)';
                    e.currentTarget.style.paddingLeft = '20px';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = isSelected ? 'rgba(211, 47, 47, 0.12)' : 'white';
                    e.currentTarget.style.paddingLeft = '16px';
                  }}
                >
                  <span style={{ fontSize: '16px' }}>{item.flag}</span>
                  <span>{item.code}</span>
                  <span style={{ marginLeft: 'auto', color: isSelected ? '#d32f2f' : '#6b7280', fontSize: '13px' }}>
                    {item.country}
                  </span>
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};

// ==================== SEARCHABLE COUNTRY SELECT ====================
const SearchableCountrySelect = ({ value, options, onChange, label, disabled = false }) => {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredOptions, setFilteredOptions] = useState([]);
  const inputRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    if (options && options.length > 0) {
      setFilteredOptions(options);
    }
  }, [options]);

  useEffect(() => {
    if (searchTerm && options) {
      const filtered = options.filter(option =>
        option.countryName?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredOptions(filtered);
    } else {
      setFilteredOptions(options || []);
    }
  }, [searchTerm, options]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        handleClose();
      }
    };

    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open]);

  const handleOpen = () => {
    if (disabled) return;
    setOpen(true);
    setSearchTerm('');
    setTimeout(() => {
      inputRef.current?.focus();
    }, 100);
  };

  const handleClose = () => {
    setOpen(false);
    setSearchTerm('');
  };

  const handleSelect = (option) => {
    onChange({
      target: {
        name: 'countryId',
        value: option.countryId.toString()
      }
    });
    handleClose();
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange({
      target: {
        name: 'countryId',
        value: ''
      }
    });
    setSearchTerm('');
  };

  const selectedItem = options?.find(opt => opt.countryId.toString() === value);

  return (
    <div ref={containerRef} style={{ position: 'relative', width: '100%' }}>
      <div style={{ position: 'relative' }}>
        <input
          ref={inputRef}
          type="text"
          value={open ? searchTerm : (selectedItem?.countryName || '')}
          onChange={(e) => {
            if (open) {
              setSearchTerm(e.target.value);
            }
          }}
          onClick={handleOpen}
          placeholder={!selectedItem ? "Search country..." : ""}
          readOnly={!open}
          disabled={disabled}
          style={{
            width: '100%',
            padding: '16.5px 40px 16.5px 14px',
            border: '1px solid #d32f2f',
            borderRadius: '4px',
            fontSize: '16px',
            outline: 'none',
            cursor: disabled ? 'not-allowed' : (open ? 'text' : 'pointer'),
            backgroundColor: disabled ? '#f5f5f5' : 'white',
            transition: 'all 0.2s',
            height: '56px',
            ...(open && {
              borderColor: '#d32f2f',
              borderWidth: '2px',
            })
          }}
        />

        {value && !open && !disabled && (
          <button
            onClick={handleClear}
            style={{
              position: 'absolute',
              right: '32px',
              top: '50%',
              transform: 'translateY(-50%)',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#9ca3af',
              transition: 'color 0.2s',
              zIndex: 2
            }}
            onMouseEnter={(e) => { e.currentTarget.style.color = '#d32f2f'; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = '#9ca3af'; }}
            type="button"
          >
            <svg style={{ width: '16px', height: '16px', pointerEvents: 'none' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}

        <svg
          style={{
            position: 'absolute',
            right: '12px',
            top: '50%',
            transform: 'translateY(-50%)',
            width: '20px',
            height: '20px',
            color: open ? '#d32f2f' : (disabled ? '#9ca3af' : '#9ca3af'),
            pointerEvents: 'none',
            zIndex: 2
          }}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          {open ? (
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          ) : (
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          )}
        </svg>
      </div>

      {open && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          right: 0,
          maxHeight: '400px',
          overflow: 'auto',
          zIndex: 1300,
          marginTop: '4px',
          backgroundColor: 'white',
          border: '1px solid #e5e7eb',
          borderRadius: '6px',
          boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)'
        }}>
          {filteredOptions.length === 0 ? (
            <div style={{ padding: '12px 16px', color: '#6b7280', fontSize: '14px' }}>
              No results found
            </div>
          ) : (
            filteredOptions.map((option, index) => {
              const isSelected = value === option.countryId.toString();
              return (
                <div
                  key={option.countryId}
                  onClick={() => handleSelect(option)}
                  style={{
                    padding: '12px 16px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: 500,
                    transition: 'all 0.2s',
                    backgroundColor: isSelected ? 'rgba(211, 47, 47, 0.12)' : 'white',
                    color: isSelected ? '#d32f2f' : '#374151',
                    borderBottom: index < filteredOptions.length - 1 ? '1px solid #f3f4f6' : 'none'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = isSelected ? 'rgba(211, 47, 47, 0.2)' : 'rgba(211, 47, 47, 0.08)';
                    e.currentTarget.style.paddingLeft = '20px';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = isSelected ? 'rgba(211, 47, 47, 0.12)' : 'white';
                    e.currentTarget.style.paddingLeft = '16px';
                  }}
                >
                  {option.countryName}
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};

// ==================== SEARCHABLE PORT SELECT ====================
const SearchablePortSelect = ({ value, options, onChange, label, disabled = false }) => {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredOptions, setFilteredOptions] = useState([]);
  const inputRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    if (options && options.length > 0) {
      setFilteredOptions(options);
    }
  }, [options]);

  useEffect(() => {
    if (searchTerm && options) {
      const filtered = options.filter(option =>
        option.portName?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredOptions(filtered);
    } else {
      setFilteredOptions(options || []);
    }
  }, [searchTerm, options]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        handleClose();
      }
    };

    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open]);

  const handleOpen = () => {
    if (disabled) return;
    setOpen(true);
    setSearchTerm('');
    setTimeout(() => {
      inputRef.current?.focus();
    }, 100);
  };

  const handleClose = () => {
    setOpen(false);
    setSearchTerm('');
  };

  const handleSelect = (option) => {
    onChange({
      target: {
        name: 'portId',
        value: option.portId.toString()
      }
    });
    handleClose();
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange({
      target: {
        name: 'portId',
        value: ''
      }
    });
    setSearchTerm('');
  };

  const selectedItem = options?.find(opt => opt.portId.toString() === value);

  return (
    <div ref={containerRef} style={{ position: 'relative', width: '100%' }}>
      <div style={{ position: 'relative' }}>
        <input
          ref={inputRef}
          type="text"
          value={open ? searchTerm : (selectedItem?.portName || '')}
          onChange={(e) => {
            if (open) {
              setSearchTerm(e.target.value);
            }
          }}
          onClick={handleOpen}
          placeholder={!selectedItem ? "Search port..." : ""}
          readOnly={!open}
          disabled={disabled}
          style={{
            width: '100%',
            padding: '16.5px 40px 16.5px 14px',
            border: '1px solid #d32f2f',
            borderRadius: '4px',
            fontSize: '16px',
            outline: 'none',
            cursor: disabled ? 'not-allowed' : (open ? 'text' : 'pointer'),
            backgroundColor: disabled ? '#f5f5f5' : 'white',
            transition: 'all 0.2s',
            height: '56px',
            ...(open && {
              borderColor: '#d32f2f',
              borderWidth: '2px',
            })
          }}
        />

        {value && !open && !disabled && (
          <button
            onClick={handleClear}
            style={{
              position: 'absolute',
              right: '32px',
              top: '50%',
              transform: 'translateY(-50%)',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#9ca3af',
              transition: 'color 0.2s',
              zIndex: 2
            }}
            onMouseEnter={(e) => { e.currentTarget.style.color = '#d32f2f'; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = '#9ca3af'; }}
            type="button"
          >
            <svg style={{ width: '16px', height: '16px', pointerEvents: 'none' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}

        <svg
          style={{
            position: 'absolute',
            right: '12px',
            top: '50%',
            transform: 'translateY(-50%)',
            width: '20px',
            height: '20px',
            color: open ? '#d32f2f' : (disabled ? '#9ca3af' : '#9ca3af'),
            pointerEvents: 'none',
            zIndex: 2
          }}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          {open ? (
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          ) : (
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          )}
        </svg>
      </div>

      {open && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          right: 0,
          maxHeight: '400px',
          overflow: 'auto',
          zIndex: 1300,
          marginTop: '4px',
          backgroundColor: 'white',
          border: '1px solid #e5e7eb',
          borderRadius: '6px',
          boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)'
        }}>
          {filteredOptions.length === 0 ? (
            <div style={{ padding: '12px 16px', color: '#6b7280', fontSize: '14px' }}>
              No ports found
            </div>
          ) : (
            filteredOptions.map((option, index) => {
              const isSelected = value === option.portId.toString();
              return (
                <div
                  key={option.portId}
                  onClick={() => handleSelect(option)}
                  style={{
                    padding: '12px 16px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: 500,
                    transition: 'all 0.2s',
                    backgroundColor: isSelected ? 'rgba(211, 47, 47, 0.12)' : 'white',
                    color: isSelected ? '#d32f2f' : '#374151',
                    borderBottom: index < filteredOptions.length - 1 ? '1px solid #f3f4f6' : 'none'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = isSelected ? 'rgba(211, 47, 47, 0.2)' : 'rgba(211, 47, 47, 0.08)';
                    e.currentTarget.style.paddingLeft = '20px';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = isSelected ? 'rgba(211, 47, 47, 0.12)' : 'white';
                    e.currentTarget.style.paddingLeft = '16px';
                  }}
                >
                  {option.portName}
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};

const AddressForm = () => {
  const [profilePic, setProfilePic] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [submitMessage, setSubmitMessage] = useState({ type: "", text: "" });
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, addressId: null });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");
  const [countryCodes, setCountryCodes] = useState([]);
  const user = JSON.parse(localStorage.getItem("user") || "{}");

  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem("authToken");
    if (!token) {
      navigate("/login");
    }
  }, [navigate]);

  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const res = await fetch(
          "https://car.amar.co.jp/api/authorization/shipping/all-countries",
          { headers: { "Accept-Language": "en" } }
        );
        const data = await res.json();
        const isoToFlag = (iso) =>
          iso.toUpperCase().replace(/./g, (c) =>
            String.fromCodePoint(c.charCodeAt(0) + 127397)
          );
        COUNTRY_CODES = data.map((c) => ({
          code: c.countryPhoneCode,
          country: c.countryName,
          flag: isoToFlag(c.isoCode),
        }));
        COUNTRY_CODES.sort((a, b) => a.country.localeCompare(b.country));
        setCountryCodes([...COUNTRY_CODES]);
      } catch (err) {
        console.error("Failed to fetch countries:", err);
      }
    };
    fetchCountries();
  }, []);

  const handleClick = () => {
    navigate("/myAccount");
  };

  // State for the main form
  const [formData, setFormData] = useState({
    id: null,
    addressLine1: "",
    addressLine2: "",
    mobileNumber: "",
    countryCode: "",
    countryId: "",
    countryName: "",
    portId: "",
    portName: "",
    city: "",
    state: "",
    pinCode: "",
  });

  // State for user addresses fetched from API
  const [additionalAddresses, setAdditionalAddresses] = useState([]);

  // State for countries and ports from API
  const [countries, setCountries] = useState([]);
  const [ports, setPorts] = useState([]);

  // Get filtered ports based on selected country
  const filteredPorts = formData.countryId
    ? ports.filter(port => port.countryId === parseInt(formData.countryId))
    : [];

  // Bearer token
  const token = localStorage.getItem("authToken");

  // API base URL
  const API_BASE_URL = 'https://car.amar.co.jp/api/authorization/user';

  // Validation function for mobile number - only digits
  // Validation function for mobile number - only digits, allow up to 15 digits
  const validateMobileNumber = (value) => {
    const digitsOnly = value.replace(/\D/g, '');
    return digitsOnly;
  };



  // Fetch countries and ports data
  const fetchCountriesAndPorts = async () => {
    try {
      // Fetch countries
      const countriesResponse = await fetch('https://car.amar.co.jp/api/authorization/shipping/countries', {
        method: "GET",
        headers: {
          accept: "application/json",
          "Accept-Language": "en",
          Authorization: `Bearer ${token}`,
        },
      });

      const countriesData = await countriesResponse.json();
      if (countriesResponse.ok) {
        setCountries(countriesData);
      } else {
        console.error("Failed to fetch countries:", countriesData);
      }

      // Fetch ports
      const portsResponse = await fetch('https://car.amar.co.jp/api/authorization/shipping/ports', {
        method: "GET",
        headers: {
          accept: "application/json",
          "Accept-Language": "en",
          Authorization: `Bearer ${token}`,
        },
      });

      const portsData = await portsResponse.json();
      if (portsResponse.ok) {
        setPorts(portsData);
      } else {
        console.error("Failed to fetch ports:", portsData);
      }
    } catch (error) {
      console.error("Error fetching countries/ports:", error);
    }
  };

  // Fetch user addresses when component mounts
  useEffect(() => {
    fetchCountriesAndPorts();
  }, []);

  useEffect(() => {
    if (countries.length > 0 && ports.length > 0) {
      fetchUserAddresses();
    }
  }, [countries, ports]);

  // Function to fetch user addresses from the API
  const fetchUserAddresses = async () => {
    setIsLoading(true);
    try {
      const response = await fetch('https://car.amar.co.jp/api/authorization/user/get-user-addresses', {
        method: "GET",
        headers: {
          accept: "application/json",
          "Accept-Language": "en",
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (response.ok) {
        console.log("Addresses fetched successfully:", data);

        if (data && data.addresses && Array.isArray(data.addresses)) {
          const formattedAddresses = data.addresses.map((address) => {
            // API returns countryCode and mobileNumber as separate fields
            const countryCode = address.countryCode || "";
            const mobileNumber = address.mobileNumber || "";

            console.log("Address ID:", address.addressId, "Country Code:", countryCode, "Mobile:", mobileNumber);

            // Get country ID and port ID from fetched data
            const countryObj = countries.find(c => c.countryName === address.countryName);
            const portObj = ports.find(p => p.portName === address.portName);

            return {
              id: address.addressId,
              addressLine1: address.addressLine1 || "",
              addressLine2: address.addressLine2 || "",
              mobileNumber: mobileNumber,
              countryCode: countryCode,
              countryId: countryObj ? countryObj.countryId.toString() : "",
              countryName: address.countryName || "",
              portId: portObj ? portObj.portId.toString() : "",
              portName: address.portName || "",
              city: address.city || "",
              state: address.state || "",
              pinCode: address.pincode || "",
              userId: address.userId
            };
          });

          console.log("All formatted addresses:", formattedAddresses);
          setAdditionalAddresses(formattedAddresses);
        } else {
          setAdditionalAddresses([]);
        }
      } else {
        console.error("Failed to fetch addresses:", data);
        setAdditionalAddresses([]);
        showNotification("error", "You don't have any saved addresses yet.");
      }
    } catch (error) {
      console.error("Error fetching addresses:", error);
      setAdditionalAddresses([]);
      showNotification("error", "Network error while loading addresses.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (event) => {
    const { name, value } = event.target;

    if (name === "mobileNumber") {
      // Only allow digits, limit to 15 characters
      const digitsOnly = validateMobileNumber(value);
      if (digitsOnly.length <= 15) {
        setFormData({ ...formData, [name]: digitsOnly });
      }
    } else if (name === "countryId") {
      const selectedCountry = countries.find(country => country.countryId === parseInt(value));
      setFormData({
        ...formData,
        [name]: value,
        countryName: selectedCountry ? selectedCountry.countryName : "",
        portId: "",
        portName: ""
      });
    } else if (name === "portId") {
      const selectedPort = ports.find(port => port.portId === parseInt(value));
      setFormData({
        ...formData,
        [name]: value,
        portName: selectedPort ? selectedPort.portName : ""
      });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const resetForm = () => {
    setFormData({
      id: null,
      addressLine1: "",
      addressLine2: "",
      mobileNumber: "",
      countryCode: "",
      countryId: "",
      countryName: "",
      portId: "",
      portName: "",
      city: "",
      state: "",
      pinCode: "",
    });
    setIsEditing(false);
  };

  const showNotification = (type, text) => {
    setSubmitMessage({ type, text });
    setShowSnackbar(true);
  };

  // Function to save address to API
  const saveAddressToAPI = async (addressData) => {
    try {
      // Prepare the data for API - combine countryCode and mobileNumber
      const payload = {
        addressLine1: addressData.addressLine1,
        addressLine2: addressData.addressLine2 || "",
        mobileNumber: `${addressData.countryCode}-${addressData.mobileNumber}`,
        city: addressData.city,
        state: addressData.state,
        pincode: addressData.pinCode,
        countryName: addressData.countryName,
        portName: addressData.portName
      };

      // Determine if we're creating or updating
      const isUpdating = isEditing && addressData.id && !addressData.id.toString().startsWith('local-');

      // Endpoint URL - different for create vs update
      const url = isUpdating
        ? `https://car.amar.co.jp/api/authorization/user/update-addresses/${addressData.id}`
        : `${API_BASE_URL}/add-user-address`;

      console.log("Sending address data to API:", payload);
      console.log("Endpoint URL:", url);

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept-Language': 'en',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();

      if (response.ok) {
        console.log("Address saved successfully:", data);
        // Refresh addresses list to get the updated data
        await fetchUserAddresses();
        return true;
      } else {
        console.error("Failed to save address:", data);
        showNotification("error", data.message || "Failed to save address");
        return false;
      }
    } catch (error) {
      console.error("Error saving address:", error);
      showNotification("error", "You Can save only 3 Address");
      return false;
    }
  };

  // Function to delete address from API
  const deleteAddressFromAPI = async (addressId) => {
    // Skip API call for local addresses that haven't been saved yet
    if (addressId.toString().startsWith('local-')) {
      const updatedAddresses = additionalAddresses.filter(address => address.id !== addressId);
      setAdditionalAddresses(updatedAddresses);
      showNotification("success", "Address removed");
      return true;
    }

    try {
      const response = await fetch(`https://car.amar.co.jp/api/authorization/user/delete-addresses/${addressId}`, {
        method: 'DELETE',
        headers: {
          'Accept-Language': 'en',
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        console.log("Address deleted successfully");
        // Update local state
        const updatedAddresses = additionalAddresses.filter(address => address.id !== addressId);
        setAdditionalAddresses(updatedAddresses);
        showNotification("success", "Address deleted successfully");
        return true;
      } else {
        const data = await response.json();
        console.error("Failed to delete address:", data);
        showNotification("error", data.message || "Failed to delete address");
        return false;
      }
    } catch (error) {
      console.error("Error deleting address:", error);
      showNotification("error", "Network error while deleting address");
      return false;
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    // Check if required fields are filled
    if (!formData.addressLine1 || !formData.mobileNumber || !formData.countryCode ||
      !formData.countryId || !formData.portId || !formData.city || !formData.state ||
      !formData.pinCode) {
      showNotification("error", "Please fill all required fields");
      return;
    }

    // Check mobile number length - must be exactly 10 digits
    if (formData.mobileNumber.length < 10 || formData.mobileNumber.length > 15) {
      showNotification("error", "Mobile number must be between 10 and 15 digits");
      return;
    }

    // Validate that mobile number contains only digits
    if (!/^\d+$/.test(formData.mobileNumber)) {
      showNotification("error", "Mobile number must contain only numbers");
      return;
    }

    setIsSubmitting(true);

    try {
      const success = await saveAddressToAPI(formData);

      if (success) {
        // Reset the form
        resetForm();
        showNotification("success", isEditing ? "Address updated successfully!" : "Address added successfully!");
      }
    } catch (error) {
      console.error("Error:", error);
      showNotification("error", "An error occurred. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdate = (address) => {
    // API returns countryCode and mobileNumber as separate fields
    const countryCode = address.countryCode || "";
    const mobileNumber = address.mobileNumber || "";

    console.log("Editing address - ID:", address.id, "Country Code:", countryCode, "Mobile:", mobileNumber);

    // Populate the form with the address data for editing
    setFormData({
      id: address.id,
      addressLine1: address.addressLine1,
      addressLine2: address.addressLine2,
      mobileNumber: mobileNumber,
      countryCode: countryCode,
      countryId: address.countryId || "",
      countryName: address.countryName || "",
      portId: address.portId || "",
      portName: address.portName || "",
      city: address.city,
      state: address.state,
      pinCode: address.pinCode
    });

    // Set editing mode
    setIsEditing(true);

    // Scroll to the form
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });

    showNotification("info", "Edit address in the form above");
  };

  const confirmDelete = (addressId) => {
    setConfirmDialog({
      open: true,
      addressId
    });
  };

  const handleConfirmDelete = async () => {
    const addressId = confirmDialog.addressId;
    setConfirmDialog({ open: false, addressId: null });

    if (addressId) {
      await deleteAddressFromAPI(addressId);
    }
  };

  const cancelDelete = () => {
    setConfirmDialog({ open: false, addressId: null });
  };

  const handleSnackbarClose = () => {
    setShowSnackbar(false);
  };

  const getAuthToken = useCallback(() => {
    return localStorage.getItem("token") ||
      sessionStorage.getItem("token") ||
      localStorage.getItem("authToken") ||
      sessionStorage.getItem("authToken");
  }, []);

  // Helper function to get initials from name
  const getInitials = useCallback((fullName) => {
    if (!fullName) return "";
    const parts = fullName.trim().split(" ");
    return parts.length > 1
      ? parts[0][0].toUpperCase() + parts[1][0].toUpperCase()
      : parts[0][0].toUpperCase();
  }, []);

  // Function to get profile image with authentication - FIXED URL
  const getProfileImageWithAuth = useCallback(async () => {
    const token = getAuthToken();
    try {
      const response = await fetch(
        `https://car.amar.co.jp/api/authorization/user/get-image/`,
        {
          headers: {
            'accept': 'application/json',
            'Accept-Language': 'en',
            "Authorization": `Bearer ${token}`,
          }
        }
      );

      console.log("Get image response status:", response.status);

      if (response.ok) {
        const blob = await response.blob();
        console.log("Blob received, size:", blob.size);
        return blob;
      } else {
        console.error("Failed to fetch image, status:", response.status);
        return null;
      }
    } catch (error) {
      console.error("Error fetching profile image:", error);
      return null;
    }
  }, [getAuthToken]);

  // Function to convert blob to base64
  const blobToBase64 = (blob) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  };

  // Handle image upload - SIMPLIFIED
  const handleImageChange = useCallback(async (event) => {
    const token = getAuthToken();
    const file = event.target.files[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      setError("Please select a valid image file");
      return;
    }

    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      setError("Image too large. Max 5MB");
      return;
    }

    // Create a local preview and cache it immediately
    const reader = new FileReader();
    reader.onload = async (e) => {
      const base64Image = e.target.result;
      setProfilePic(base64Image);
      // Cache the preview immediately in localStorage
      localStorage.setItem("profileImageData", base64Image);
    };
    reader.readAsDataURL(file);

    const formData = new FormData();
    formData.append("profile-image", file);

    try {
      setLoading(true);
      setError("");
      setSuccess("");

      if (!token) {
        throw new Error("Authentication token missing. Please log in again.");
      }

      console.log("Uploading image...");

      const uploadUrl = 'https://car.amar.co.jp/api/authorization/user/upload/profile-photo';
      const res = await fetch(uploadUrl, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${token}`
        },
        body: formData
      });

      if (!res.ok) {
        throw new Error(`Upload failed: ${res.status}`);
      }

      const data = await res.json();
      console.log("Upload response:", data);

      if (data?.uniqueFileName || data?.fileName) {
        const fileName = data?.uniqueFileName || data?.fileName;
        localStorage.setItem("profileImageFileName", fileName);

        // After successful upload, fetch the updated image
        setTimeout(async () => {
          try {
            const blob = await getProfileImageWithAuth();
            if (blob) {
              const base64Image = await blobToBase64(blob);
              localStorage.setItem("profileImageData", base64Image);
              setProfilePic(base64Image);
            }
          } catch (error) {
            console.log("Could not fetch updated image, keeping local preview");
          }
        }, 1000);

        setSuccess("Uploaded successfully!");
        setTimeout(() => {
          setSuccess("");
        }, 3000);

      } else {
        throw new Error("No filename returned from server");
      }
    } catch (err) {
      console.error("Upload error:", err);
      setError(err.message || "Something went wrong");
      // Revert to previous cached image on error
      const cachedImage = localStorage.getItem("profileImageData");
      if (cachedImage) {
        setProfilePic(cachedImage);
      }
    } finally {
      setLoading(false);
    }
  }, [getAuthToken, getProfileImageWithAuth]);

  // Load user profile image on component mount
  useEffect(() => {
    console.log("=== Component mounted, loading profile image ===");

    // STEP 1: Load cached image IMMEDIATELY
    const cachedImageData = localStorage.getItem("profileImageData");
    console.log("localStorage check:", cachedImageData ? `Has cached image (${cachedImageData.substring(0, 50)}...)` : "No cached image");

    if (cachedImageData && cachedImageData.startsWith('data:image')) {
      console.log("✓ Setting cached image to state immediately");
      setProfilePic(cachedImageData);
      setLoading(false);
    } else {
      console.log("✗ No valid cached image found");
      setLoading(true);
    }

    // STEP 2: Fetch from API in background
    const loadFromAPI = async () => {
      const token = getAuthToken();
      if (!token) {
        console.warn("✗ No auth token available");
        setLoading(false);
        return;
      }

      try {
        console.log("→ Fetching fresh image from API...");
        const blob = await getProfileImageWithAuth();

        if (blob && blob.size > 0) {
          console.log("✓ Received blob from API, size:", blob.size);
          const base64Image = await blobToBase64(blob);
          console.log("✓ Converted to base64, length:", base64Image.length);

          // Save to localStorage
          localStorage.setItem("profileImageData", base64Image);
          console.log("✓ Saved to localStorage");

          // Update state
          setProfilePic(base64Image);
          console.log("✓ Updated state with fresh image");
        } else {
          console.log("✗ No valid blob received from API");
          // Keep cached image if we have one
          if (cachedImageData && cachedImageData.startsWith('data:image')) {
            console.log("→ Keeping cached image");
            setProfilePic(cachedImageData);
          } else {
            setProfilePic(null);
          }
        }
      } catch (err) {
        console.error("✗ Error loading from API:", err);
        // Always keep cached image on error
        if (cachedImageData && cachedImageData.startsWith('data:image')) {
          console.log("→ Error occurred, keeping cached image");
          setProfilePic(cachedImageData);
        }
      } finally {
        setLoading(false);
        console.log("=== Profile image loading complete ===");
      }
    };

    // Small delay to ensure state is set from cache first
    const timer = setTimeout(() => {
      loadFromAPI();
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  return (
    <Box
      className="container-main-AllSeeModels"
      sx={{
        position: "relative",
        width: "100%",
      }}
    >
      <Box sx={{ marginBottom: "0%" }}><Notes /></Box>

      <Box
        className="image-container-AllSeeModels"
        sx={{
          position: "relative",
          width: "100%",
          height: { xs: "30vh", sm: "40vh", md: "50vh" },
          backgroundColor: "black",
        }}
      >
        <Box
          component="img"
          src="/image/shutterstock.png"
          alt="shutter"
          sx={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            opacity: 0.3,
          }}
        />

        <Box
          sx={{
            position: "absolute",
            top: { xs: "5%", sm: "5%", md: "5%" },
            left: { xs: "3%", sm: "5%", md: "5%" },
            margin: 0,
            color: "white",
            fontSize: { xs: "11px", sm: "13px", md: "15px" },
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            padding: { xs: "4px 8px", sm: "5px 10px", md: "5px 10px" },
            borderRadius: "5px",
          }}
        >
          <Typography
            component="span"
            sx={{
              opacity: 0.5,
              marginRight: { xs: "2px", sm: "5px", md: "5px" },
            }}
          >
            <Link to="http://3.110.209.177" style={{ fontSize: "inherit", color: "white", textDecoration: "none" }}>
              Home/
            </Link>
          </Typography>

          <Link to="https://car.amar.co.jp/MyAccount" style={{ fontSize: "inherit", color: "white", textDecoration: "none" }}>
            My Account
          </Link>

          / MyAddress

        </Box>

        <Box
          className="support-paragraph-AllSeeModels"
          sx={{
            position: "absolute",
            margin: "auto",
            color: "white",
            left: "50%",
            top: "50%",
            transform: "translate(-50%, -50%)",
            fontSize: { xs: "1.5rem", sm: "2rem", md: "30px" },
            fontFamily: "Roboto",
            fontWeight: "700",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: { xs: "10px", sm: "15px", md: "20px" },
            textAlign: "center",
            width: "90%"
          }}
        >
          Addresses
          <button
            onClick={handleClick}
            style={{
              backgroundColor: "#EB2329",
              color: "white",
              border: "none",
              padding: window.innerWidth < 600 ? "8px 16px" : window.innerWidth < 960 ? "9px 18px" : "10px 20px",
              fontSize: window.innerWidth < 600 ? "14px" : window.innerWidth < 960 ? "15px" : "16px",
              borderRadius: "8px",
              cursor: "pointer",
              textTransform: "uppercase",
              transition: "background-color 0.3s ease",
            }}
            onMouseOver={(e) => (e.target.style.backgroundColor = "#c41f23")}
            onMouseOut={(e) => (e.target.style.backgroundColor = "#EB2329")}
          >
            My Account
          </button>
        </Box>

      </Box>

      {/* MAIN CONTENT CONTAINER WITH OVERLAP */}
      <Box
        sx={{
          position: "relative",
          width: { xs: "95%", sm: "92%", md: "90%" },
          backgroundColor: "#FFFFFF",
          border: { xs: "1px solid #EBEBEB", md: "2px solid #EBEBEB" },
          borderRadius: { xs: "8px", md: "10px" },
          marginLeft: "auto",
          marginRight: "auto",
          marginTop: { xs: "-50px", sm: "-60px", md: "-75px" },
          marginBottom: { xs: "20px", sm: "30px", md: "40px" },
          zIndex: 10,
          pb: { xs: 2, sm: 3, md: 4 }
        }}
      >
        {/* Profile Section */}
        <Grid container spacing={{ xs: 2, sm: 3, md: 4 }} sx={{ p: { xs: 1.5, sm: 2.5, md: 4 }, pt: { xs: 3, sm: 4, md: 5 } }}>
          {/* Profile Picture Upload (Left Side) */}
          <Grid item xs={12} md={4} sx={{ textAlign: "center" }}>
            <label htmlFor="profile-upload" style={{ cursor: "pointer" }}>
              <input
                type="file"
                id="profile-upload"
                accept="image/*"
                onChange={handleImageChange}
                style={{ display: "none" }}
              />
              <Box
                sx={{
                  width: { xs: 150, sm: 180, md: 250 },
                  height: { xs: 150, sm: 180, md: 250 },
                  borderRadius: "50%",
                  overflow: "hidden",
                  margin: "auto",
                  border: { xs: "1px solid #ccc", md: "2px solid #ccc" },
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "#f9f9f9",
                  "&:hover": {
                    border: { xs: "1px solid #888", md: "2px solid #888" },
                  },
                  position: "relative",
                }}
              >
                {loading && !profilePic ? (
                  <Typography>Loading...</Typography>
                ) : profilePic ? (
                  <img
                    src={profilePic}
                    alt="Profile"
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                    onError={(e) => {
                      console.error("Image failed to render");
                      // Try to reload from localStorage one more time
                      const cachedImage = localStorage.getItem("profileImageData");
                      if (cachedImage && cachedImage !== profilePic) {
                        console.log("Attempting to reload from localStorage");
                        setProfilePic(cachedImage);
                      }
                    }}
                  />
                ) : (
                  <Avatar
                    sx={{
                      width: "100%",
                      height: "100%",
                      fontSize: { xs: 32, sm: 40, md: 48 },
                    }}
                  >
                    {getInitials(formData?.firstName && formData?.lastName
                      ? `${formData.firstName} ${formData.lastName}`
                      : user?.fullName) || "U"}
                  </Avatar>
                )}

                <Box
                  sx={{
                    position: "absolute",
                    bottom: 0,
                    left: 0,
                    right: 0,
                    backgroundColor: "rgba(0,0,0,0.5)",
                    color: "white",
                    padding: "8px 0",
                    textAlign: "center",
                  }}
                >
                  <Typography variant="body2">
                    {profilePic ? " " : "Upload Photo"}
                  </Typography>
                </Box>
              </Box>
            </label>

            {error && (
              <Typography color="error" mt={1}>
                {error}
              </Typography>
            )}
            {success && (
              <Typography color="primary" mt={1}>
                {success}
              </Typography>
            )}
          </Grid>

          {/* Main Address Form */}
          <Grid item xs={12} md={6}>
            <Typography variant="h5" sx={{ mb: 2, color: "red", marginLeft: "1%", fontWeight: "700" }}>
              {isEditing ? "Update Address" : "Add New Address"}
            </Typography>

            <Box component="form" sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {/* Address Line 1 and Address Line 2 in same row */}
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    name="addressLine1"
                    label="Address Line 1 *"
                    fullWidth
                    value={formData.addressLine1}
                    onChange={handleChange}
                    required
                    InputLabelProps={{ shrink: true }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                          borderColor: '#d32f2f',
                        },
                        '&:hover fieldset': {
                          borderColor: '#d32f2f',
                        },
                      },
                      '& .MuiInputLabel-root': {
                        color: '#d32f2f',
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    name="addressLine2"
                    label="Address Line 2"
                    fullWidth
                    value={formData.addressLine2}
                    onChange={handleChange}
                    InputLabelProps={{ shrink: true }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                          borderColor: '#d32f2f',
                        },
                        '&:hover fieldset': {
                          borderColor: '#d32f2f',
                        },
                      },
                      '& .MuiInputLabel-root': {
                        color: '#d32f2f',
                      },
                    }}
                  />
                </Grid>
              </Grid>

              {/* Country Code and Mobile Number in same row */}
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <SearchableCountryCodeSelect
                    value={formData.countryCode}
                    onChange={handleChange}
                    options={countryCodes}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    name="mobileNumber"
                    label="Mobile Number *"
                    fullWidth
                    value={formData.mobileNumber}
                    onChange={handleChange}
                    placeholder="Enter your mobile number"
                    inputProps={{
                      maxLength: 15,
                      inputMode: 'numeric',
                      pattern: '[0-9]*'
                    }}
                    required
                    error={formData.mobileNumber.length > 0 && formData.mobileNumber.length < 10}
                    helperText={formData.mobileNumber.length > 0 && formData.mobileNumber.length < 10 ? "Minimum 10 digits required" : ""}
                    InputLabelProps={{ shrink: true }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                          borderColor: '#d32f2f',
                        },
                        '&:hover fieldset': {
                          borderColor: '#d32f2f',
                        },
                      },
                      '& .MuiInputLabel-root': {
                        color: '#d32f2f',
                      },
                      '& .MuiFormHelperText-root': {
                        color: '#d32f2f',
                      },
                    }}
                  />
                </Grid>
              </Grid>

              {/* Country and Port in same row */}
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography
                    variant="body2"
                    sx={{
                      mb: 0.5,
                      color: '#d32f2f',
                      fontWeight: 500,
                      fontSize: '0.875rem'
                    }}
                  >
                    Select Country *
                  </Typography>
                  <SearchableCountrySelect
                    value={formData.countryId}
                    options={countries}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography
                    variant="body2"
                    sx={{
                      mb: 0.5,
                      color: '#d32f2f',
                      fontWeight: 500,
                      fontSize: '0.875rem'
                    }}
                  >
                    Preferred Sea Port *
                  </Typography>
                  <SearchablePortSelect
                    value={formData.portId}
                    options={filteredPorts}
                    onChange={handleChange}
                    disabled={!formData.countryId}
                  />
                </Grid>
              </Grid>

              {/* City, State, and Pin Code in one row */}
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <TextField
                    name="city"
                    label="City *"
                    fullWidth
                    value={formData.city}
                    onChange={handleChange}
                    required
                    InputLabelProps={{ shrink: true }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                          borderColor: '#d32f2f',
                        },
                        '&:hover fieldset': {
                          borderColor: '#d32f2f',
                        },
                      },
                      '& .MuiInputLabel-root': {
                        color: '#d32f2f',
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    name="state"
                    label="State *"
                    fullWidth
                    value={formData.state}
                    onChange={handleChange}
                    required
                    InputLabelProps={{ shrink: true }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                          borderColor: '#d32f2f',
                        },
                        '&:hover fieldset': {
                          borderColor: '#d32f2f',
                        },
                      },
                      '& .MuiInputLabel-root': {
                        color: '#d32f2f',
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    name="pinCode"
                    label="Pin Code *"
                    fullWidth
                    value={formData.pinCode}
                    onChange={handleChange}
                    required
                    InputLabelProps={{ shrink: true }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                          borderColor: '#d32f2f',
                        },
                        '&:hover fieldset': {
                          borderColor: '#d32f2f',
                        },
                      },
                      '& .MuiInputLabel-root': {
                        color: '#d32f2f',
                      },
                    }}
                  />
                </Grid>
              </Grid>

              {/* Buttons */}
              <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                <Button
                  fullWidth
                  variant="contained"
                  sx={{ backgroundColor: "#eb2329", ":hover": { backgroundColor: "#d02026" } }}
                  type="submit"
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (isEditing ? "UPDATING..." : "ADDING...") : (isEditing ? "UPDATE ADDRESS" : "ADD ADDRESS")}
                </Button>

                {isEditing && (
                  <Button
                    fullWidth
                    variant="outlined"
                    sx={{ borderColor: "#eb2329", color: "#eb2329" }}
                    onClick={resetForm}
                  >
                    CANCEL
                  </Button>
                )}
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* Additional Address Forms in Grid Layout */}
      <Box
        sx={{
          width: "90%",
          position: "relative",
          left: "50%",
          transform: "translateX(-50%)",
          marginBottom: "40px"
        }}
      >
        <Typography variant="h5" sx={{ mb: 4, color: "red", fontWeight: "700" }}>
          Your Saved Addresses
        </Typography>

        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress sx={{ color: "#eb2329" }} />
          </Box>
        ) : additionalAddresses.length > 0 ? (
          <Grid container spacing={3}>
            {additionalAddresses.map((address, index) => (
              <Grid item xs={12} md={4} key={address.id}>
                <Box
                  sx={{
                    backgroundColor: "#FFFFFF",
                    border: "2px solid #EBEBEB",
                    borderRadius: "10px",
                    padding: "20px",
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    position: "relative"
                  }}
                >
                  <IconButton
                    color="error"
                    onClick={() => confirmDelete(address.id)}
                    sx={{ position: "absolute", top: 10, right: 10 }}
                  >
                    <DeleteIcon />
                  </IconButton>

                  <Typography variant="h6" sx={{ mb: 2, fontWeight: "600" }}>
                    Address {index + 1}
                  </Typography>

                  <Box sx={{ flexGrow: 1 }}>
                    <Typography sx={{ mb: 1 }}>
                      <strong>Address:</strong> {address.addressLine1}
                    </Typography>
                    {address.addressLine2 && (
                      <Typography sx={{ mb: 1 }}>
                        {address.addressLine2}
                      </Typography>
                    )}
                    <Typography sx={{ mb: 1 }}>
                      <strong>City:</strong> {address.city}, {address.state}, {address.pinCode}
                    </Typography>
                    <Typography sx={{ mb: 1 }}>
                      <strong>Country:</strong> {address.countryName}
                    </Typography>
                    <Typography sx={{ mb: 1 }}>
                      <strong>Preferred Port:</strong> {address.portName}
                    </Typography>
                    {/* Display mobile number with country code */}
                    <Typography sx={{ mb: 1, fontWeight: '500' }}>
                      <strong>Mobile:</strong> {address.countryCode && address.mobileNumber
                        ? `${address.countryCode}-${address.mobileNumber}`
                        : (address.mobileNumber || 'Not provided')}
                    </Typography>
                  </Box>

                  <Button
                    fullWidth
                    variant="contained"
                    sx={{ mt: 2, backgroundColor: "#eb2329", ":hover": { backgroundColor: "#d02026" } }}
                    onClick={() => handleUpdate(address)}
                  >
                    UPDATE
                  </Button>
                </Box>
              </Grid>
            ))}
          </Grid>
        ) : (
          <Box sx={{ textAlign: "center", py: 4, backgroundColor: "#f8f8f8", borderRadius: "10px", padding: "20px" }}>
            <Typography variant="body1">You don't have any saved addresses yet.</Typography>
          </Box>
        )}
      </Box>

      {/* Snackbar for notifications */}
      <Snackbar
        open={showSnackbar}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={submitMessage.type === "error" ? "error" : (submitMessage.type === "info" ? "info" : "success")}
          sx={{ width: '100%' }}
        >
          {submitMessage.text}
        </Alert>
      </Snackbar>

      {/* Confirmation Dialog for Delete */}
      <Dialog
        open={confirmDialog.open}
        onClose={cancelDelete}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          Are you sure you want to delete this address?
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelDelete} color="primary">
            Cancel
          </Button>
          <Button onClick={handleConfirmDelete} color="error" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      <FooterManual />
    </Box>
  );
};

export default AddressForm;